// Clipboard - copies text to the clipboard from [data-clipboard] triggers and
// confirms it with a short-lived tooltip.
//
// Behavioral port of v1 modules/Clipboard.js: the confirmation was a throwaway
// tippy.js instance, v2 uses our own tooltip (modules/tooltip.ts over
// @floating-ui/dom) the same way.
//
// HTML contract (unchanged from v1): [data-clipboard] with the text taken from
// data-clipboard-text, or from the value/textContent of data-clipboard-target;
// data-clipboard-msg-success / data-clipboard-msg-error override the messages.
// Triggers are disabled when the Clipboard API is unavailable (it needs a
// secure context).

import { SingleTooltip, type TooltipInstance } from './tooltip';

export interface ClipboardConfig {
  /** Confirmation content; HTML is allowed (v1 default is a check icon). */
  successMsg: string;
  /** Shown when reading the text or writing to the clipboard fails. */
  errorMsg: string;
  /** How long the confirmation stays visible, ms. */
  duration: number;
}

const defaultConfig: ClipboardConfig = {
  successMsg: '<i class="far fa-check-circle fa-lg"></i>',
  errorMsg: 'Chyba pri kopírovaní',
  duration: 2000,
};

export default class Clipboard {
  selector: string;

  config: ClipboardConfig;

  private handlers: Array<{ element: Element; handler: EventListener }> = [];

  private feedback = new Map<Element, { tooltip: TooltipInstance; timer: number }>();

  constructor(selector = '[data-clipboard]', config: Partial<ClipboardConfig> = {}) {
    this.selector = selector;
    this.config = { ...defaultConfig, ...config };

    this._init();
  }

  static isSupported(): boolean {
    return Boolean(navigator.clipboard?.writeText);
  }

  destroy(): void {
    this.handlers.forEach(({ element, handler }) => {
      element.removeEventListener('click', handler);
    });
    this.handlers = [];

    this.feedback.forEach(({ tooltip, timer }) => {
      clearTimeout(timer);
      tooltip.destroy();
    });
    this.feedback.clear();
  }

  update(): void {
    this.destroy();
    this._init();
  }

  private _init(): void {
    const elements = [...document.querySelectorAll<HTMLElement>(this.selector)];

    if (!Clipboard.isSupported()) {
      elements.forEach(element => element.setAttribute('disabled', 'true'));
      return;
    }

    elements.forEach(element => {
      const handler = this._createClickHandler(element);
      element.addEventListener('click', handler);
      this.handlers.push({ element, handler });
    });
  }

  private _createClickHandler(element: HTMLElement): EventListener {
    return async (event: Event): Promise<void> => {
      event.preventDefault();

      try {
        const selector = element.getAttribute('data-clipboard-target');
        const target = selector
          ? document.querySelector<HTMLInputElement | HTMLElement>(selector)
          : null;

        const text =
          element.getAttribute('data-clipboard-text')
          || (target as HTMLInputElement | null)?.value
          || target?.textContent
          || '';

        await navigator.clipboard.writeText(text);

        this._showFeedback(
          element,
          element.getAttribute('data-clipboard-msg-success') || this.config.successMsg,
        );
      } catch {
        this._showFeedback(
          element,
          element.getAttribute('data-clipboard-msg-error') || this.config.errorMsg,
        );
      }
    };
  }

  /** Pinned tooltip that removes itself after config.duration. */
  private _showFeedback(element: HTMLElement, content: string): void {
    this._clearFeedback(element);

    const tooltip = new SingleTooltip(element, {
      // v1 used tippy's 'auto-end'; floating-ui has no auto placement, flip()
      // in modules/floating.ts covers the same need.
      placement: 'top-end',
      arrow: false,
      allowHTML: true,
      interactive: false,
      delay: 0,
      trigger: 'manual',
      showOnCreate: true,
      hideOnClick: true,
      content: () => content,
    });

    const timer = window.setTimeout(() => {
      tooltip.destroy();
      this.feedback.delete(element);
    }, this.config.duration);

    this.feedback.set(element, { tooltip, timer });
  }

  private _clearFeedback(element: Element): void {
    const previous = this.feedback.get(element);
    if (!previous) return;

    clearTimeout(previous.timer);
    previous.tooltip.destroy();
    this.feedback.delete(element);
  }
}
